import { uuidv4 } from "./utils"
import { encodeWAV, IsEmptyBuffer } from "./utils"

export default class AssemblyAI {
    constructor(opts) {
        this.Opts = opts
        this.AccessKey = opts?.key
        this.WS = []
        this.lenOfChunks = 0
        this.transcript = {} // transcripts {transcript_id: { transcript data (summary a few chunks) }, ...}
        this.transcriptIds = [] // IDs of current prase transcription [channel_index: id of current phrase]
        this.transcriptChunks = {} // IDs of current chunk transcription: {chanel_index: { start_time: { chunk transcript data } }, ...}
        this.roles = [] // [channel_index: role]
        this.audioContext = [] // [channel_index: audiocontext]
        this.startQueue = [] // [channel_index: array of audio chunks which collected while ws was connect]
        this.utteranceDelay = [] // [channel_index: timeout]

        window.addEventListener("transcriber_split_transcription", () => this.splitTranscription())
    }

    initWS = chid => {
        const params = {
            sample_rate: 16000,
            encoding: "pcm_s16le",
            token: this.AccessKey,
            format_turns: true, // Whether to return formatted final transcripts
        }
        const urlParams = new URLSearchParams([...Object.entries(params)])
        const ws = new WebSocket(`wss://streaming.assemblyai.com/v3/ws?${urlParams}`)
        ws.chid = chid
        ws.onopen = this.onOpen(chid)
        ws.onerror = this.onError(chid)
        ws.onclose = this.onClose(chid)
        ws.onmessage = this.onMessage(chid)
        return ws
    }

    onOpen = chid => () => {
        // this.WS[chid].send(JSON.stringify({ type: "UpdateConfiguration", end_utterance_silence_thresholdmax_turn_silence: 1200 }))
    }
    onError = chid => () => (this.WS[chid].started = false)
    onClose = chid => () => (this.WS[chid].started = false)

    onMessage = chid => event => {
        const msg = JSON.parse(event.data)
        switch (msg.type) {
            case "Turn":
                if (!msg?.transcript) return

                const chunk = {
                    start: (msg?.words || []).reduce((pv, cv) => (cv?.start < pv ? cv?.start : pv), 0),
                    end: (msg?.words || []).reduce((pv, cv) => (cv?.end > pv ? cv?.end : pv), 0),
                    is_final: msg.end_of_turn && msg?.turn_is_formatted,
                    text: msg.transcript,
                    channel: chid,
                }
                chunk.duration = chunk.end - chunk.start

                if (this.transcriptChunks?.[chid]?.[chunk.start]?.is_final) return

                this.latestEnd = chunk.end

                if (this.transcriptChunks?.[chunk.channel]) {
                    this.transcriptChunks[chunk.channel][chunk.start] = chunk
                } else {
                    this.transcriptChunks[chunk.channel] = { [chunk.start]: chunk }
                }

                this.summarizeTranscriptChunks(chunk.end, chunk.channel, chunk.is_final)
                break

            case "Begin":
                this.WS[chid].started = true

                if (this.startQueue[chid].length > 0) {
                    for (let chunk of this.startQueue[chid]) {
                        this.WS[chid].send(chunk)
                    }
                    this.startQueue[chid] = undefined
                }

                break

            default:
                console.log("unknown message", msg)
        }
    }

    OnLine = chid => this.WS[chid]?.readyState === WebSocket.OPEN

    FinishPhrase = chid => {
        if (Date.now() - (this.utteranceDelay[chid] || 0) > 1200) {
            this.WS[chid].send(JSON.stringify({ type: "ForceEndpoint" }))
            this.utteranceDelay[chid] = Date.now()
        }
    }

    Send = (chid, chunk) => {
        if (!this.OnLine(chid) && !this.startQueue[chid]) {
            return
        }

        if (!this.WS[chid]?.started && this.startQueue[chid]) {
            this.startQueue[chid].push(chunk)
        } else {
            this.WS[chid].send(chunk)
        }
    }

    Finish = () => {
        for (let chid = 0; chid < this.roles.length; chid++) {
            if (this.OnLine(chid)) {
                this.WS[chid].send(JSON.stringify({ type: "Terminate" }))
            }
            this.audioContext[chid]?.close()
            this.WS[chid]?.close()
        }
        window.removeEventListener("transcriber_split_transcription", () => this.splitTranscription())
    }

    summarizeTranscriptChunks = (end, channel, final, recursive = false) => {
        if (!this.transcriptChunks[channel]) return

        const transcript = {
            role: this.roles[channel],
            text: "",
            id: this.transcriptIds[channel],
            ts: this.transcript[this.transcriptIds[channel]]?.ts || new Date().getTime(),
            channel,
        }

        for (const chunk of Object.values(this.transcriptChunks[channel])) {
            if (chunk.start >= end || chunk.channel !== channel) continue
            if (final && !chunk.is_final) continue
            if (transcript.start === undefined) transcript.start = chunk.start
            if (transcript.end === undefined || chunk.end > transcript.end) transcript.end = chunk.end

            transcript.text = transcript.text + " " + chunk.text
            transcript.text = transcript.text.trim()

            if (final) {
                delete this.transcriptChunks[channel][chunk.start]
            }
        }

        if (transcript.text.length === 0) {
            return
        }

        this.transcript[this.transcriptIds[channel]] = transcript

        if (final || recursive) {
            this.transcriptIds[channel] = uuidv4()
        }

        window.dispatchEvent(
            new CustomEvent("call_intelligence_call_transcription", {
                detail: transcript,
            })
        )

        if (!recursive && this.roles.length > 1) {
            for (let i = 0; i < this.roles.length; i++) {
                if (i === channel) continue
                if (this.transcript[this.transcriptIds[i]]?.text) {
                    this.FinishPhrase(i)
                }
                this.summarizeTranscriptChunks(end, i, true, true)
            }
        }
    }

    splitTranscription = () => {
        for (let i = 0; i < this.roles.length; i++) {
            this.summarizeTranscriptChunks(this.latestEnd, i, true, true)
        }
    }

    async AddAudioTrack(role, audioTrack) {
        const chid = this.roles.push(role) - 1
        this.transcriptIds.push(uuidv4())
        this.WS.push(this.initWS(chid))
        this.audioContext.push(new AudioContext({ sampleRate: 16000 }))
        this.startQueue.push([])

        const source = this.audioContext[chid].createMediaStreamSource(new MediaStream([audioTrack]))
        const processor = this.audioContext[chid].createScriptProcessor(4096, 1, 1)

        processor.onaudioprocess = async e => {
            const audioData = e.inputBuffer.getChannelData(0)
            if (IsEmptyBuffer(audioData)) {
                return
            }
            const numChannels = e.inputBuffer.numberOfChannels
            const sampleRate = e.inputBuffer.sampleRate

            const ebuffer = encodeWAV(audioData, 1, sampleRate, numChannels, 16)
            this.Send(chid, new Uint8Array(ebuffer))
        }

        source.connect(processor)
        processor.connect(this.audioContext[chid].destination)
    }
}
